import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Calendar, Clock, User } from "lucide-react";
import DateSelector from "./components/DateSelector";
import TimeSelector from "./components/TimeSelector";
import ContactForm from "./components/ContactForm";
import BookingConfirmation from "./components/BookingConfirmation";
import "./BookingFlow.css";

gsap.registerPlugin(ScrollTrigger);

const BookingFlow = () => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    date: null,
    time: null,
    duration: 60, // Default to 60 minutes
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    clinicName: "",
    website: "",
    services: [],
    message: "",
  });

  const steps = [
    { number: 1, label: "Pick Date", icon: <Calendar size={20} /> },
    { number: 2, label: "Select Time", icon: <Clock size={20} /> },
    { number: 3, label: "Your Details", icon: <User size={20} /> },
  ];

  const handleDateSelect = (date) => {
    setBookingData({ ...bookingData, date });
    setCurrentStep(2);
  };

  const handleTimeSelect = (time, duration) => {
    setBookingData({ ...bookingData, time, duration });
    setCurrentStep(3);
  };

  const handleFormSubmit = (formData) => {
    setBookingData({ ...bookingData, ...formData });
    setCurrentStep(4);
    // Here we'll integrate Google Calendar API later
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  useEffect(() => {
    if (!isHomePage || !headerRef.current) return;

    const ctx = gsap.context(() => {
      const headerElements = headerRef.current.querySelectorAll(".animate-in");
      gsap.fromTo(
        headerElements,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [isHomePage]);

  return (
    <section className="booking-flow" ref={sectionRef}>
      <div className="booking-flow__container">
        {isHomePage && (
          <div className="booking-flow__header" ref={headerRef}>
            <div className="booking-flow__badge animate-in">GET STARTED</div>
            <h2 className="booking-flow__headline animate-in">Book Your Free Strategy Call</h2>
            <p className="booking-flow__description animate-in">
              Tell us about your clinic and we'll be in touch within 24 hours.
            </p>
          </div>
        )}

        {/* Progress Steps */}
        {currentStep < 4 && (
          <div className="booking-flow__steps">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`booking-flow__step ${
                  currentStep === step.number
                    ? "booking-flow__step--active"
                    : ""
                } ${
                  currentStep > step.number
                    ? "booking-flow__step--completed"
                    : ""
                }`}
              >
                <div className="booking-flow__step-icon">{step.icon}</div>
                <div className="booking-flow__step-label">{step.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Step Content */}
        <div className="booking-flow__content">
          {currentStep === 1 && (
            <DateSelector onSelectDate={handleDateSelect} />
          )}

          {currentStep === 2 && (
            <TimeSelector
              selectedDate={bookingData.date}
              onSelectTime={handleTimeSelect}
              onBack={handleBack}
            />
          )}

          {currentStep === 3 && (
            <ContactForm
              bookingData={bookingData}
              onSubmit={handleFormSubmit}
              onBack={handleBack}
            />
          )}

          {currentStep === 4 && (
            <BookingConfirmation bookingData={bookingData} />
          )}
        </div>
      </div>
    </section>
  );
};

export default BookingFlow;
